import React, { useState, useEffect } from 'react';
import { Estado } from '../../types/sistema';
import { sistemaService } from '../../services/sistemaService';
import { X, MapPin, Save, Loader2, AlertCircle } from 'lucide-react';

interface EstadoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  estado: Estado | null;
  onSuccess: () => void;
}

export const EstadoFormModal: React.FC<EstadoFormModalProps> = ({
  isOpen,
  onClose,
  estado,
  onSuccess,
}) => {
  const [nome, setNome] = useState('');
  const [sigla, setSigla] = useState('');
  const [codigoIbge, setCodigoIbge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNome(estado?.nome || '');
      setSigla(estado?.sigla || (estado as any)?.uf || '');
      setCodigoIbge(estado?.codigo_ibge || '');
      setError(null);
    }
  }, [isOpen, estado]);

  if (!isOpen) return null;

  const isEditing = Boolean(estado && estado.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setError('Por favor, informe o nome do estado.');
      return;
    }
    if (!sigla.trim()) {
      setError('Por favor, informe a sigla (UF) do estado.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        nome: nome.trim(),
        sigla: sigla.trim().toUpperCase(),
        codigo_ibge: codigoIbge.trim() || undefined,
      };

      if (isEditing && estado) {
        const res = await sistemaService.updateEstado(estado.id, payload);
        if (!res.success) {
          setError(res.error || 'Erro ao atualizar estado.');
          setLoading(false);
          return;
        }
      } else {
        const res = await sistemaService.createEstado(payload);
        if (!res.success) {
          setError(res.error || 'Erro ao cadastrar estado.');
          setLoading(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar estado:', err);
      setError(err.message || 'Erro inesperado ao salvar estado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 text-red-600 rounded-xl">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">
                {isEditing ? 'Editar Estado (UF)' : 'Novo Estado (UF)'}
              </h3>
              <p className="text-xs text-slate-500">
                {isEditing ? `Alterando dados do estado #${estado?.id}` : 'Cadastre uma nova Unidade Federativa'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Nome do Estado <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: São Paulo, Rio de Janeiro, Minas Gerais..."
                required
                autoFocus
                className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400 font-medium transition-all"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Sigla (UF) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={2}
                  value={sigla}
                  onChange={(e) => setSigla(e.target.value.toUpperCase())}
                  placeholder="Ex: SP"
                  required
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm uppercase font-bold focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Código IBGE <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={codigoIbge}
                  onChange={(e) => setCodigoIbge(e.target.value)}
                  placeholder="Ex: 35"
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400 font-mono transition-all"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs hover:shadow-md transition-all disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Salvar Alterações' : 'Cadastrar Estado'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
